// src/components/PunjabLocationPicker.js
// -----------------------------------------------------------------------------
// Cascading Region > District > Meeting Location (AC) dropdowns for Punjab.
// Uses the bundled geography JSON (no network call, so it works regardless of
// how auth is stored). Emits { region, district, acName, acNo } on every change.
//
//   <PunjabLocationPicker
//     value={{ region, district, acName, acNo }}
//     onChange={({ region, district, acName, acNo }) => { ... }}
//   />
// -----------------------------------------------------------------------------

import React, { useMemo } from "react";
import geo from "../data/punjabGeography.json";
import "../css/punjabLocation.css";

export default function PunjabLocationPicker({ value = {}, onChange, required = true }) {
  const region = value.region || "";
  const district = value.district || "";
  const acName = value.acName || "";

  const regions = useMemo(() => Object.values(geo.regions || {}), []);
  const districts = useMemo(() => {
    const r = geo.regions?.[region];
    return r ? Object.keys(r.districts).sort() : [];
  }, [region]);
  const acs = useMemo(
    () => geo.regions?.[region]?.districts?.[district] || [],
    [region, district]
  );

  const emit = (next) =>
    onChange &&
    onChange({
      region: next.region ?? region,
      district: next.district ?? district,
      acName: next.acName ?? acName,
      acNo: next.acNo ?? value.acNo ?? null,
    });

  return (
    <div className="pj-picker">
      <div className="pj-field">
        <label>Region {required && <span className="pj-req">*</span>}</label>
        <select
          value={region}
          onChange={(e) => emit({ region: e.target.value, district: "", acName: "", acNo: null })}
        >
          <option value="">Select Region</option>
          {regions.map((r) => (
            <option key={r.region} value={r.region}>
              {r.label || r.region}
            </option>
          ))}
        </select>
      </div>

      <div className="pj-field">
        <label>District {required && <span className="pj-req">*</span>}</label>
        <select
          value={district}
          disabled={!region}
          onChange={(e) => emit({ district: e.target.value, acName: "", acNo: null })}
        >
          <option value="">{region ? "Select District" : "Select Region first"}</option>
          {districts.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      <div className="pj-field">
        <label>
          Meeting Location (Assembly Constituency) {required && <span className="pj-req">*</span>}
        </label>
        <select
          value={acName}
          disabled={!district}
          onChange={(e) => {
            const ac = acs.find((a) => a.ac_name === e.target.value);
            emit({ acName: e.target.value, acNo: ac ? ac.ac_no : null });
          }}
        >
          <option value="">{district ? "Select Constituency" : "Select District first"}</option>
          {acs.map((a) => (
            <option key={`${a.ac_no}-${a.ac_name}`} value={a.ac_name}>
              {a.ac_no ? `${a.ac_no} — ` : ""}
              {a.display || a.ac_name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
